import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, FileText } from "lucide-react";

const INTAKE_FIELDS = [
  "Name & phone",
  "Email",
  "City in SWFL",
  "Issue type (e.g., not cooling)",
  "Urgency (today / this week / flexible)",
  "System age",
  "Is the AC still blowing cool air?",
  "Preferred contact method",
  "Notes for the technician",
  "Optional photo of the unit",
];

const INTAKE_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/7942e3c3124650064c34709dc44e8be7836c82a2dcd39d297b024286607f2108.png";

export default function SmartIntakePreview() {
  return (
    <section
      data-testid="smart-intake-section"
      aria-labelledby="smart-intake-heading"
      className="relative bg-white py-20 sm:py-24"
    >
      <div className="container-page grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <span className="pill">
            <FileText className="h-3 w-3 text-aqua" />
            Smart intake
          </span>
          <h2
            id="smart-intake-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-[2.5rem]"
          >
            A service request that actually explains the problem.
          </h2>
          <p className="mt-4 max-w-xl text-base text-navy/70 sm:text-lg">
            Instead of a generic &ldquo;name and number&rdquo; box, the intake
            collects everything a contractor needs to triage and quote
            confidently &mdash; before the callback.
          </p>

          <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {INTAKE_FIELDS.map((f) => (
              <li
                key={f}
                className="flex items-start gap-2 text-sm text-navy/85"
              >
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-aqua/15 text-aqua-deep">
                  <Check className="h-3 w-3" />
                </span>
                {f}
              </li>
            ))}
          </ul>

          <Link
            to="/request-service"
            data-testid="smart-intake-cta"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-sm font-semibold text-white shadow-soft transition-colors hover:bg-navy-soft"
          >
            See the full intake
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl border border-mist-line bg-mist shadow-lift">
              <img
                src={INTAKE_IMAGE}
                alt="Illustration of a smart HVAC service-request form on a phone with airflow accent lines."
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>

            {/* floating Demo Lead Preview badge */}
            <div className="absolute -right-3 -top-3 rotate-[3deg] rounded-2xl border border-sun/40 bg-white px-4 py-3 shadow-lift sm:-right-6 sm:-top-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sun">
                Demo Lead Preview
              </p>
              <p className="mt-0.5 font-display text-sm font-bold text-navy">
                Maria H. &middot; Cape Coral
              </p>
              <p className="text-[11px] text-navy/65">
                AC not cooling &middot; today if available
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
